import { useMsal } from '@azure/msal-react';
import { Avatar, Button, Stack, Typography } from '@mui/material';
import LogRocket from 'logrocket';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserService, configService, getAuthToken } from '../../service';
import CustomHeader from '../customer-header/customer-header.component';
import LogoutIcon from './log-out-icon.png';
import UserInfoIcon from './navigation-login-50x50.png';

const HeaderUserInfo = () => {
    const { instance, accounts } = useMsal();
    const [currentUser, setCurrentUser] = useState();
    const [impersonatedUser, setImpersonatedUser] = useState(false);

    useEffect(() => {
        const user = sessionStorage.getItem('impersonatedUser');

        if (!user) return;
        setImpersonatedUser(true);
    }, []);

    useEffect(() => {
        (async () => {
            await getCurrentUser();
        })();
    }, []);

    useEffect(() => {
        if (!currentUser) return;

        LogRocket.identify(currentUser.Email, {
            name: `${currentUser.FirstName} ${currentUser.LastName}`,
            email: currentUser.Email,
            username: currentUser.Company
        });
    }, [currentUser]);

    const getCurrentUser = async () => {
        const authToken = await getAuthToken(instance, accounts);
        const user = await UserService.getCurrentUser(authToken);

        if (!user) {
            return;
        }

        setCurrentUser(user);
    };

    const handleLogout = () => {
        instance.logoutRedirect();
    };

    return (
        <>
            <Stack spacing={0} direction={'row'} alignItems={'center'} justifyContent={'flex-end'} style={{ backgroundColor: '#002A4D', height: '100%', paddingRight: '10px' }}>
                {currentUser && (
                    <Button
                        variant={'contained'}
                        size="small"
                        startIcon={<Avatar src={UserInfoIcon} sx={{ height: '26px', width: '26px' }} />}
                        sx={{ textTransform: 'none', fontWeight: 'bold', boxShadow: 'none' }}
                    >
                        {impersonatedUser ? `${currentUser.Email}` : `${currentUser.Company}`}
                    </Button>
                )}

                <Button
                    variant={'contained'}
                    onClick={() => handleLogout()}
                    size="small"
                    startIcon={<Avatar src={LogoutIcon} sx={{ height: '26px', width: '26px' }} />}
                    sx={{ textTransform: 'none', fontWeight: 'bold', boxShadow: 'none' }}
                >
                    Logout
                </Button>
            </Stack>
        </>
    );
};

const SubHeader = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const onBrandItemClick = (item) => {
        if (!item) {
            return;
        }

        switch (item) {
            case 'dashboard':
                navigate('/', {
                    state: {
                        keyName: 'dashboard'
                    }
                });
                break;
            default:
                alert(`You clicked : ${item}.`);
        }
    };

    const onPortStateBrandItemClick = (item) => {
        if (!item) return;

        navigate('/port-state-control');
    };

    const onMenuItemClick = (item) => {
        if (!item) {
            return;
        }

        switch (item) {
            case 'MyFleet':
                navigate('/vessel', {
                    state: {
                        keyName: 'MyFleet'
                    }
                });
                break;

            case 'MyPortStateControl':
                navigate('/port-state-control', {
                    state: {
                        keyName: 'MyPortStateControl'
                    }
                });
                break;
            default:
                alert(`You clicked : ${item}.`);
        }
    };

    return (
        <>
            {location.pathname === '/port-state-control' ? (
                <CustomHeader brandName="ABS Port State Control" type="sub" onBrandClick={onPortStateBrandItemClick} onMenuItemClick={onMenuItemClick} />
            ) : (
                <CustomHeader
                    brandName="Home"
                    type="sub"
                    menuOptions={[
                        {
                            text: 'My Vessels',
                            value: 'MyFleet',
                            position: 'left'
                        }
                    ]}
                    onBrandClick={onBrandItemClick}
                    onMenuItemClick={onMenuItemClick}
                />
            )}
        </>
    );
};

const Header = () => {
    const [headerHeight, setHeaderHeight] = useState('auto');
    const [headerProps, setHeaderProps] = useState({
        width: '100%',
        title: 'Header',
        scrolling: 'auto',
        frameBorder: '0'
    });

    const { instance, accounts } = useMsal();
    const [currentUser, setCurrentUser] = useState();
    const [impersonatedUser, setImpersonatedUser] = useState();

    useEffect(() => {
        (async () => {
            await getCurrentUser();
            const user = sessionStorage.getItem('impersonatedUser');

            if (!user) return;
            setImpersonatedUser(user);
        })();
    }, []);

    const getCurrentUser = async () => {
        const authToken = await getAuthToken(instance, accounts);
        const user = await UserService.getCurrentUser(authToken);

        if (!user) {
            return;
        }

        setCurrentUser(user);
    };

    const dispatchMessage = (payload) => {
        const headerContent = document.getElementById('ifrmHeader');
        headerContent?.contentWindow?.postMessage(payload, '*');
    };

    const messageListener = (e) => {
        if (e.data?.event) {
            const isResize = e.data?.event === 'resizeheader';
            const isProps = e.data?.event === 'headerProps' && e.data?.props && typeof e.data.props === 'object';
            isResize && setHeaderHeight(e.data.height);
            isProps && setHeaderProps((currentProps) => ({ ...currentProps, ...e.data.props }));
        }
    };

    const clickListener = () => setHeaderHeight(70);
    const resizeListener = () => dispatchMessage({ windowResize: { width: window.innerWidth } });

    const triggerOnLoad = () => {
        const user = 'user';
        const host = window.location;
        const environment = 'https://dev-regworldmap.apps.eagle.org/';
        const copyright = `ABS &copy; ${new Date().getFullYear()} All Rights Reserved`;
        const buildTime = 'buildTime';
        const payLoad = { headerLoaded: true, copyright, buildTime, user, host, environment };
        dispatchMessage(JSON.parse(JSON.stringify(payLoad)));
    };

    const listeners = [
        ['message', messageListener],
        ['resize', resizeListener],
        ['click', clickListener]
    ];

    useEffect(() => {
        listeners.forEach((l) => window.addEventListener(l[0], l[1]));
        return () => listeners.forEach((l) => window.removeEventListener(l[0], l[1]));
    }, []);

    return (
        <>
            <div id="HeaderWrapper" style={{ height: '70px' }}>
                <iframe
                    src={configService.appConfig.aemUrl}
                    id="ifrmHeader"
                    height={headerHeight}
                    onLoad={triggerOnLoad}
                    {...headerProps}
                    style={{ position: 'fixed', width: '100%', top: 0, left: 0, zIndex: 1042 }}
                />
            </div>

            <div style={{ position: 'fixed', height: '70px', top: 0, right: 0, zIndex: 1050, minWidth: '260px' }}>
                <HeaderUserInfo />
            </div>

            <SubHeader />
            {impersonatedUser && (
                <Stack direction={'row'} alignContent={'center'} justifyContent={'center'} alignItems={'center'} style={{ backgroundColor: '#DA1F33', color: '#ffffff', padding: 5 }} gap={2}>
                    <Typography>
                        Impersonating <b>{currentUser.Company}</b>
                    </Typography>
                </Stack>
            )}
        </>
    );
};

export default Header;
